import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Space, 
  Button, 
  message, 
  Spin,
  Tag,
  List,
  Input,
  Avatar,
  Modal,
  Popconfirm
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserOutlined,
  SendOutlined,
  EditFilled,
  MessageOutlined,
  FileOutlined
} from '@ant-design/icons';
import { announcementService } from '../../services/announcementService';
import { commentService } from '../../services/commentService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Announcement, Comment, Attachment } from '../../types';
import { formatLocalizedDateTime } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import '../../styles/contentStyles.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * 공지사항 상세보기 컴포넌트
 * 
 * 이 컴포넌트는 공지사항의 상세 내용을 표시하고, 댓글 기능을 제공합니다.
 * 관리자는 공지사항을 수정, 삭제, 승인/거부할 수 있습니다.
 * 
 * 주요 기능:
 * - 공지사항 상세 내용 표시
 * - 첨부파일 표시 및 다운로드
 * - 댓글 작성, 수정, 삭제
 * - 관리자 기능 (수정, 삭제, 승인/거부)
 * - 조회수 증가
 * 
 * @component
 */
const AnnouncementDetail: React.FC = () => {
  // URL 파라미터에서 공지사항 ID 추출
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate(); // 페이지 이동을 위한 훅
  const { isAdmin, user, isAuthenticated } = useAuth(); // 인증 정보 (관리자 여부, 현재 사용자, 인증 상태)
  const { t, i18n } = useTranslation();

  // 상태 관리
  const [announcement, setAnnouncement] = useState<Announcement | null>(null); // 공지사항 데이터
  const [attachments, setAttachments] = useState<Attachment[]>([]); // 첨부파일 목록
  const [comments, setComments] = useState<Comment[]>([]); // 댓글 목록
  const [loading, setLoading] = useState(true); // 공지사항 로딩 상태
  const [commentLoading, setCommentLoading] = useState(false); // 댓글 로딩 상태
  const [newComment, setNewComment] = useState(''); // 새 댓글 내용
  const [editingComment, setEditingComment] = useState<string | null>(null); // 수정 중인 댓글 ID
  const [editContent, setEditContent] = useState(''); // 수정할 댓글 내용
  const [isEditModalVisible, setIsEditModalVisible] = useState(false); // 댓글 수정 모달 표시 여부

  /**
   * 공지사항 상세 정보를 서버에서 가져오는 함수
   * 
   * @async
   * @function fetchAnnouncementDetail
   * @returns {Promise<void>}
   */
  const fetchAnnouncementDetail = useCallback(async () => {
    if (!id) return; // ID가 없으면 함수 종료
    
    setLoading(true); // 로딩 시작
    try {
      const data = await announcementService.getAnnouncement(id); // API 호출
      setAnnouncement(data); // 상태 업데이트
      
      // 첨부파일 별도 조회 (에디터 이미지 제외)
      try {
        const attachmentData = await attachmentService.getAnnouncementAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {
        // 첨부파일 조회 실패 시 빈 배열로 설정
        setAttachments([]);
      }
    } catch (error: any) {
      message.error(t('announcements.DETAIL_SECTION.error.announcementLoad')); // 에러 메시지 표시
    } finally {
      setLoading(false); // 로딩 종료
    }
  }, [id, t]);

  /**
   * 댓글 목록을 서버에서 가져오는 함수
   * 
   * @async
   * @function fetchComments
   * @returns {Promise<void>}
   */
  const fetchComments = useCallback(async () => {
    if (!id || id === 'new') return; // ID가 없거나 'new'이면 함수 종료
    
    setCommentLoading(true); // 댓글 로딩 시작
    try {
      const data = await commentService.getAnnouncementComments(id); // API 호출
      setComments(data); // 상태 업데이트
    } catch (error: any) {
      message.error(t('announcements.DETAIL_SECTION.error.commentLoad')); // 에러 메시지 표시
    } finally {
      setCommentLoading(false); // 댓글 로딩 종료
    }
  }, [id, t]);

  /**
   * 컴포넌트 마운트 시 공지사항과 댓글을 가져옴
   */
  useEffect(() => {
    if (id) {
      fetchAnnouncementDetail();
      fetchComments();
    }
  }, [id, fetchAnnouncementDetail, fetchComments]);

  /**
   * 공지사항 삭제 처리 함수
   * 
   * @async
   * @function handleDelete
   * @returns {Promise<void>}
   */
  const handleDelete = async () => {
    if (!announcement) return; // 공지사항이 없으면 함수 종료
    
    try {
      await announcementService.deleteAnnouncement(announcement.announcement_id); // API 호출
      message.success(t('announcements.DETAIL_SECTION.success.announcementDelete')); // 성공 메시지
      navigate('/announcements'); // 목록 페이지로 이동
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.DETAIL_SECTION.error.announcementDelete')); // 에러 메시지
    }
  };

  /**
   * 공지사항 승인 처리 함수
   * 
   * @async
   * @function handleApprove
   * @returns {Promise<void>}
   */
  const handleApprove = async () => {
    if (!announcement) return; // 공지사항이 없으면 함수 종료
    
    try {
      await announcementService.approveAnnouncement(announcement.announcement_id); // API 호출
      message.success(t('announcements.DETAIL_SECTION.success.announcementApprove')); // 성공 메시지
      fetchAnnouncementDetail(); // 공지사항 정보 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.DETAIL_SECTION.error.announcementApprove')); // 에러 메시지
    }
  };

  /**
   * 공지사항 승인 취소 처리 함수
   * 
   * @async
   * @function handleUnapprove
   * @returns {Promise<void>}
   */
  const handleUnapprove = async () => {
    if (!announcement) return; // 공지사항이 없으면 함수 종료
    
    try {
      await announcementService.unapproveAnnouncement(announcement.announcement_id); // API 호출
      message.success(t('announcements.DETAIL_SECTION.success.announcementUnapprove')); // 성공 메시지
      fetchAnnouncementDetail(); // 공지사항 정보 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.DETAIL_SECTION.error.announcementUnapprove')); // 에러 메시지
    }
  };

  /**
   * 새 댓글 작성 처리 함수
   * 
   * @async
   * @function handleSubmitComment
   * @returns {Promise<void>}
   */
  const handleSubmitComment = async () => {
    if (!id || id === 'new' || !newComment.trim()) return; // ID나 댓글 내용이 없으면 함수 종료
    
    try {
      await commentService.createComment({
        content: newComment,
        announcementId: id,
      }); // API 호출
      message.success(t('announcements.DETAIL_SECTION.success.commentCreate')); // 성공 메시지
      setNewComment(''); // 댓글 입력창 초기화
      fetchComments(); // 댓글 목록 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.DETAIL_SECTION.error.commentCreate')); // 에러 메시지
    }
  };

  /**
   * 댓글 수정 처리 함수
   * 
   * @async
   * @function handleEditComment
   * @returns {Promise<void>}
   */
  const handleEditComment = async () => {
    if (!editingComment || !editContent.trim()) return; // 수정할 댓글 ID나 내용이 없으면 함수 종료
    
    try {
      await commentService.updateComment(editingComment, {
        content: editContent,
      }); // API 호출
      message.success(t('announcements.DETAIL_SECTION.success.commentUpdate')); // 성공 메시지
      setEditingComment(null); // 수정 중인 댓글 ID 초기화
      setEditContent(''); // 수정할 내용 초기화
      setIsEditModalVisible(false); // 모달 닫기
      fetchComments(); // 댓글 목록 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.DETAIL_SECTION.error.commentUpdate')); // 에러 메시지
    }
  };

  /**
   * 댓글 삭제 처리 함수
   * 
   * @async
   * @function handleDeleteComment
   * @param {string} commentId - 삭제할 댓글의 ID
   * @returns {Promise<void>}
   */
  const handleDeleteComment = async (commentId: string) => {
    try {
      await commentService.deleteComment(commentId); // API 호출
      message.success(t('announcements.DETAIL_SECTION.success.commentDelete')); // 성공 메시지
      fetchComments(); // 댓글 목록 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.DETAIL_SECTION.error.commentDelete')); // 에러 메시지
    }
  };

  /**
   * 댓글 수정 모달을 여는 함수
   * 
   * @function openEditModal
   * @param {Comment} comment - 수정할 댓글 객체
   */
  const openEditModal = (comment: Comment) => {
    setEditingComment(comment.comment_id); // 수정할 댓글 ID 설정
    setEditContent(comment.content); // 수정할 내용 설정
    setIsEditModalVisible(true); // 모달 표시
  };

  /**
   * 첨부파일 다운로드 함수
   * 
   * @function handleDownloadAttachment
   * @param {Attachment} attachment - 다운로드할 첨부파일 객체
   */
  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, attachment.file_name);
    } catch (error: any) {
      message.error('첨부파일 다운로드에 실패했습니다.');
    }
  };

  // 로딩 중일 때 표시할 컴포넌트
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('announcements.DETAIL_SECTION.loading.announcementLoad')}</div>
      </div>
    );
  }

  // 공지사항이 없을 때 표시할 컴포넌트
  if (!announcement) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('announcements.DETAIL_SECTION.notFound')}</Title>
        <Button type="primary" onClick={() => navigate('/announcements')}>
          {t('button.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* 공지사항 상세 내용 카드 */}
      <Card>
        {/* 헤더 섹션 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          {/* 뒤로가기 버튼 */}
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/announcements')}
            style={{ marginRight: 16 }}
          >
            {t('button.backToList')}
          </Button>
          
          {/* 관리자용 작업 버튼들 */}
          {isAdmin && (
            <Space>
              {/* 수정 버튼 */}
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => navigate(`/announcements/${announcement.announcement_id}/edit`)}
                disabled={announcement.is_approved}
                title={announcement.is_approved ? '승인된 문서는 수정할 수 없습니다' : ''}
              >
                {t('button.edit')}
              </Button>
              
              {/* 승인/거부 버튼 */}
              {announcement.is_approved ? (
                <Button 
                  onClick={handleUnapprove}
                  danger
                >
                  {t('button.unapprove')}
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  onClick={handleApprove}
                >
                  {t('button.approve')}
                </Button>
              )}
              
              {/* 삭제 버튼 */}
              <Popconfirm
                title={t('confirm.deleteAnnouncement')}
                onConfirm={handleDelete}
                okText={t('button.delete')}
                cancelText={t('button.cancel')}
              >
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                >
                  {t('button.delete')}
                </Button>
              </Popconfirm>
            </Space>
          )}
        </div>

        {/* 공지사항 정보 섹션 */}
        <div style={{ marginBottom: 24 }}>
          {/* 제목 */}
          <Title level={2} style={{ marginBottom: 16 }}>
            {announcement.title}
          </Title>
          
          {/* 메타 정보 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Space>
                             {/* 작성자 */}
               <Text type="secondary">
                 <UserOutlined /> {announcement.createdBy?.role === 'ADMIN' ? 'admin' : (announcement.createdBy?.username || t('common.unknown'))}
               </Text>
              
              {/* 작성일 */}
              <Text type="secondary">
                {t('common.createdAt')}: {formatLocalizedDateTime(announcement.created_at, i18n.language)}
              </Text>
              
              {/* 수정일 (수정된 경우에만 표시) */}
              {announcement.updated_at && announcement.updated_at !== announcement.created_at && (
                <Text type="secondary">
                  {t('common.updatedAt')}: {formatLocalizedDateTime(announcement.updated_at, i18n.language)}
                </Text>
              )}
              
              {/* 조회수 */}
              <Text type="secondary">
                {t('common.viewCount')}: {announcement.view_count || 0}
              </Text>
            </Space>
            
            {/* 승인 상태 태그 */}
            <Tag color={announcement.is_approved ? 'green' : 'orange'}>
              {announcement.is_approved ? t('status.approved') : t('status.pending')}
            </Tag>
          </div>
        </div>

        {/* 공지사항 내용 */}
        <div style={{ marginBottom: 24 }}>
          <div 
            className="preview-content"
            dangerouslySetInnerHTML={{ __html: announcement.content }}
            style={{ 
              fontSize: '16px', 
              lineHeight: '1.8',
              fontFamily: 'monospace'
            }}
          />
        </div>

        {/* 첨부파일 섹션 */}
        {attachments.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>{t('announcements.detail.attachments')}</Title>
            <Card>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.attachment_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>📎</span>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {decodeURIComponent(attachment.file_name)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {attachmentService.formatFileSize(attachment.file_size)} • {formatLocalizedDateTime(attachment.uploaded_at, i18n.language)}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      {t('announcements.detail.download')}
                    </Button>
                  </div>
                ))}
              </Space>
            </Card>
          </div>
        )}
      </Card>

      {/* 댓글 섹션 */}
      <Card title={t('comments.title', { count: comments.length })} style={{ marginTop: 16 }}>
        {/* 새 댓글 작성 폼 */}
        <div style={{ marginBottom: 16 }}>
          {isAuthenticated ? (
            <>
              <TextArea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('comments.placeholder')}
                rows={3}
                style={{ marginBottom: 8 }}
              />
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                onClick={handleSubmitComment}
                disabled={!newComment.trim()}
              >
                {t('comments.write')}
              </Button>
            </>
          ) : (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '8px',
              border: '1px dashed #d9d9d9'
            }}>
              <MessageOutlined style={{ fontSize: '24px', color: '#999', marginBottom: '8px' }} />
              <div style={{ color: '#666', marginBottom: '12px' }}>
                {t('comments.loginRequired')}
              </div>
                                <Button 
                    type="primary" 
                    onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
                  >
                    {t('auth.login')}
                  </Button>
            </div>
          )}
        </div>

        {/* 댓글 목록 */}
        <List
          dataSource={comments}
          loading={commentLoading}
          itemLayout="horizontal"
          renderItem={(comment) => (
            <List.Item
              actions={[
                                 // 댓글 작성자이거나 관리자인 경우 수정 버튼 표시
                 (comment.user?.username === user?.username || isAdmin) && (
                  <Button 
                    type="link" 
                    size="small"
                    icon={<EditFilled />}
                    onClick={() => openEditModal(comment)}
                  >
                    {t('button.edit')}
                  </Button>
                ),
                                 // 댓글 작성자이거나 관리자인 경우 삭제 버튼 표시
                 (comment.user?.username === user?.username || isAdmin) && (
                  <Popconfirm
                    title={t('confirm.deleteComment')}
                    onConfirm={() => handleDeleteComment(comment.comment_id)}
                    okText={t('button.delete')}
                    cancelText={t('button.cancel')}
                  >
                    <Button 
                      type="link" 
                      danger 
                      size="small"
                    >
                      {t('button.delete')}
                    </Button>
                  </Popconfirm>
                )
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={
                  <Space>
                                         <Text strong>{comment.user?.role === 'ADMIN' ? 'admin' : (comment.user?.username || t('common.unknown'))}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {formatLocalizedDateTime(comment.created_at, i18n.language)}
                    </Text>
                    {/* 수정된 댓글인 경우 표시 */}
                    {comment.updated_at && comment.updated_at !== comment.created_at && (
                                          <Text type="secondary" style={{ fontSize: '12px' }}>
                      ({t('comments.edited')}: {formatLocalizedDateTime(comment.updated_at, i18n.language)})
                    </Text>
                    )}
                  </Space>
                }
                description={comment.content}
              />
            </List.Item>
          )}
        />
      </Card>

      {/* 댓글 수정 모달 */}
      <Modal
        title={t('comments.edit')}
        open={isEditModalVisible}
        onOk={handleEditComment}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingComment(null);
          setEditContent('');
        }}
        okText={t('button.edit')}
        cancelText={t('button.cancel')}
      >
        <TextArea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          placeholder={t('comments.editPlaceholder')}
          rows={4}
        />
      </Modal>
    </div>
  );
};

export default AnnouncementDetail; 